/**
 * The words the print panel puts on screen.
 *
 * Extracted from PrintMaps.vue because these are not presentation — they are
 * claims about what the user is about to get, and several of them were wrong
 * before ("one sheet per day" when a fixed scale tiles a long day; "Scale to
 * fit" when the page could work out the actual number). A claim worth making
 * is worth testing, and it cannot be tested inline in a component.
 *
 * Pure functions of explicit inputs: no store, no map, no refs.
 */
import { formatScale } from './printMapGeometry';
import { describeFittedScale, scalesDiffer } from './printScalePlan';
import type { SheetMode } from './printSheetQueue';

/** How many days of a fan-out overflow one sheet, out of how many. */
export interface OverflowDays {
  over: number;
  total: number;
}

/** "1:25k" — the compact form used on the scale buttons. */
export function shortScale(denominator: number): string {
  return denominator >= 1000 ? `1:${Math.round(denominator / 1000)}k` : `1:${denominator}`;
}

/** "Covers 4.5 × 5.9 km of ground", or the fitted-scale stand-in. */
export function coverageHint(coverage: { widthKm: number; heightKm: number } | null): string {
  if (!coverage) return 'A scale is chosen to fit each sheet';

  return `Covers ${coverage.widthKm.toFixed(1)} × ${coverage.heightKm.toFixed(1)} km of ground`;
}

export interface ScaleHintInput {
  fitted: boolean;
  mode: SheetMode;
  coverage: string;
  /** Denominators a fitted job will print at — one per day for a fan-out. */
  fittedScales: number[];
  overflow: OverflowDays;
}

/**
 * The line under the scale buttons.
 *
 * Fixed scale: says how much ground a sheet covers, and — the part that used
 * to be a vague "longer days will run to several sheets" — exactly how many
 * days overflow at this scale.
 *
 * Fitted: names the scale, or the range of scales, rather than saying nothing.
 */
export function scaleHint({ fitted, mode, coverage, fittedScales, overflow }: ScaleHintInput): string {
  if (!fitted) {
    if (mode === 'single') return coverage;

    const { over, total } = overflow;
    if (over === 0) return `${coverage} — each day still fits one sheet`;
    if (over >= total) return `${coverage} — every day will run to several sheets`;
    return `${coverage} — ${over} of ${total} days will run to several sheets`;
  }

  const label = describeFittedScale(fittedScales);

  if (mode === 'per_day') {
    // The honest caveat. Different pages at different scales is the right
    // behaviour — it is what keeps each day on one sheet — but it is a
    // surprise in a printed set unless you are told.
    return scalesDiffer(fittedScales)
      ? `Each day is scaled to fit its own sheet, so pages print at different scales (${label})`
      : `Each day is scaled to fit its own sheet — ${label} for all of them`;
  }

  return label ? `The whole walk fitted to the page at about ${label}` : 'The whole walk fitted to the page';
}

/**
 * The hint on the "Every day" mode option.
 *
 * Only promises one sheet per day when that is actually true — pin a fixed
 * scale and the strip mapper tiles a long day across several.
 */
export function perDayHint({ over, total }: OverflowDays): string {
  if (over === 0) return "One sheet per day, framed on that day's walk";
  if (over >= total) return 'Framed a day at a time — every day runs to several sheets at this scale';
  return `Framed a day at a time — ${over} of ${total} days run to several sheets at this scale`;
}

export interface ActionInput {
  mode: SheetMode;
  dayCount: number;
  pileSize: number;
  overflow: OverflowDays;
  /**
   * PDFs the pile comes out as. One unless the user changed page size or
   * orientation partway through collecting, since a single document cannot
   * mix them. Defaults to 1 so callers that never had a pile are unaffected.
   */
  pileJobs?: number;
}

export function primaryActionLabel({ mode, dayCount, pileSize, pileJobs = 1 }: ActionInput): string {
  if (mode === 'per_day') return `Print all ${dayCount} days`;
  if (mode === 'whole_route') return 'Print the whole route';
  // The label sits directly above the hint, so it has to agree with it. Saying
  // "as one PDF" over a hint explaining the pile splits into two is the exact
  // surprise this was meant to remove.
  if (pileSize > 1) return `Print ${pileSize} sheets as ${pileJobs > 1 ? `${pileJobs} PDFs` : 'one PDF'}`;
  if (pileSize === 1) return 'Print 1 sheet';
  return 'Print this sheet';
}

/** "Printing 4 sheets as one PDF — it will appear in the queue." */
export function combinedSuccessMessage(sheetCount: number, jobCount: number): string {
  return jobCount > 1
    ? `Printing ${sheetCount} sheets as ${jobCount} PDFs — they will appear in the queue.`
    : `Printing ${sheetCount} sheets as one PDF — it will appear in the queue.`;
}

/**
 * "3 sheets could not be queued" — counted in sheets, not jobs.
 *
 * `createPrintMaps` reports failures per job index, and a combined pile packs
 * many sheets into one job. Reporting the raw failure count told someone whose
 * twelve-sheet pile failed that "1 sheet(s) could not be queued".
 */
export function failedSheetCount(jobs: Array<{ pages?: unknown }>, failedIndexes: number[]): number {
  return failedIndexes.reduce((total, index) => {
    const pages = jobs[index]?.pages;
    return total + (Array.isArray(pages) ? pages.length : 1);
  }, 0);
}

export function primaryActionHint({ mode, pileSize, overflow, pileJobs = 1 }: ActionInput): string {
  if (mode === 'per_day') {
    // "a page per day" is a promise the fixed-scale path cannot keep.
    return overflow.over === 0
      ? 'One PDF, a page per day, sent to the print queue'
      : 'One PDF, at least a page per day, sent to the print queue';
  }
  if (mode === 'whole_route') return 'One PDF covering the full walk';
  if (pileSize) {
    // A pile that straddles page sizes cannot be one document, and finding
    // that out from the queue rather than the button is a nasty surprise.
    return pileJobs > 1
      ? `${pileSize} sheets, as ${pileJobs} PDFs — one per page size and orientation`
      : `The ${pileSize} sheets you have collected, as one PDF`;
  }
  return 'The area inside the red box on the map';
}

/** "north 1.4° E of true, sheet rotated to it" — empty when north is up. */
export function gridNorthNote(signedDegrees: number): string {
  if (!signedDegrees) return '';

  return `north ${Math.abs(signedDegrees).toFixed(1)}° ${signedDegrees >= 0 ? 'E' : 'W'} of true, sheet rotated to it`;
}

/** The map readout's headline: the fixed scale, or what fitting will produce. */
export function readoutScale(denominator: number | null, fittedScales: number[]): string {
  if (denominator) return formatScale(denominator);

  return describeFittedScale(fittedScales) || 'Scale to fit';
}

/**
 * The modes available for a given source.
 *
 * A hike with days can fan out; any hike can print whole; anyone can frame a
 * sheet by hand. Below two options there is no question to ask, which is why
 * the panel hides the card entirely.
 */
export interface ModeOption {
  value: SheetMode;
  label: string;
  hint: string;
}

export function availableModes(
  source: { multiDay: boolean; dayCount: number } | null,
  perDayHintText: string,
): ModeOption[] {
  const modes: ModeOption[] = [];

  if (source?.multiDay) {
    modes.push({ value: 'per_day', label: `Every day (${source.dayCount})`, hint: perDayHintText });
  }
  if (source) {
    modes.push({ value: 'whole_route', label: 'Whole route', hint: 'The full walk, on as few sheets as it takes' });
  }
  modes.push({ value: 'single', label: 'This map area', hint: 'Frame it yourself on the map' });

  return modes;
}

export function hasModeChoice(modes: ModeOption[]): boolean {
  return modes.length > 1;
}
